import { sleep } from 'lib/utils/async/delay';
import qs from 'query-string';
import { FormErrorResponse } from '../errors/error-response';
import {
  AuthenticationError,
  BadRequestError,
  ForbiddenError,
  HTTPError,
  InternalServerError,
  MethodNotAllowedError,
  NotFoundError
} from '../exceptions/http-exceptions';

export interface QueryArgs {
  [x: string]: string | string[] | number | boolean | undefined | null;
}

export type PagingOpts = {
  page?: number;
  pageSize?: number;
};

export interface RequestOpts {
  args?: QueryArgs;
  headers?: HeadersInit;
  cache?: RequestCache;
  data?: any;
  signal?: AbortSignal;
}

/**
 * Base class for API implementations.
 *
 * @author jonmd
 */
export class BaseAPI {
  private static root: string = '';
  private static csrfToken: string | undefined = undefined;

  private static fakeDelay: () => number = () => 0;
  private static unauthorizedHooks: (() => void)[] = [];

  static setRoot(root: string) {
    this.root = root;
  }

  static setFakeDelay(millis: number, jitter: number = 0) {
    if (millis === 0 && jitter === 0) {
      // No delay
      this.fakeDelay = () => {
        return 0;
      };
    } else {
      // Add delay as a combination of a fixed delay plus random jitter
      this.fakeDelay = () => {
        const extra = jitter > 0 ? Math.random() * jitter : 0;
        const total = millis + extra;
        return Math.floor(total);
      };
    }
  }

  static setCSRFToken(token: string) {
    this.csrfToken = token;
  }

  static addUnauthorizedHook(hook: () => void) {
    this.unauthorizedHooks.push(hook);
  }

  private static _fireUnauthorizedHooks() {
    // Fire asynchronously of each other, so that the hooks can
    // crash without affecting the others
    this.unauthorizedHooks.forEach((hook) => setTimeout(hook, 0));
  }

  private static _prepare(method: string, opts: RequestOpts): RequestInit {
    const r: RequestInit = {};

    // HTTP method
    r.method = method;

    // Include user credentials
    r.credentials = 'same-origin';

    // Cache
    r.cache = opts.cache;

    // Abort signal
    r.signal = opts.signal;

    // Headers for POST, PUT, PATCH and DELETE requests as we
    // always send JSON data in the body
    if (
      (method === 'POST' ||
        method === 'PUT' ||
        method === 'PATCH' ||
        method === 'DELETE') &&
      this.csrfToken !== undefined
    ) {
      // Add headers
      r.headers = {
        ['Accept']: 'application/json',
        ['Content-Type']: 'application/json',
        ['X-CSRFToken']: this.csrfToken,
        ...opts.headers
      };
    } else {
      // Add headers
      r.headers = {
        ['Accept']: 'application/json',
        ['Content-Type']: 'application/json',
        ...opts.headers
      };
    }

    // Body
    if (opts.data !== undefined) {
      r.body = JSON.stringify(opts.data);
    }

    return r;
  }

  private static _prepareUrl(url: string, opts: RequestOpts): string {
    // Format query arguments, if any
    if (opts.args) {
      const query = qs.stringify(opts.args, {
        skipNull: true
      });
      url = url + '?' + query;
    }

    // Prepend the API root path
    return this.root + url;
  }

  static async _get(path: string, opts: RequestOpts = {}) {
    return this._request('GET', path, opts);
  }

  static async _post(path: string, opts: RequestOpts) {
    return this._request('POST', path, opts);
  }

  static async _put(path: string, opts: RequestOpts) {
    return this._request('PUT', path, opts);
  }

  static async _patch(path: string, opts: RequestOpts) {
    return this._request('PATCH', path, opts);
  }

  static async _delete(path: string, opts: RequestOpts) {
    return this._request('DELETE', path, opts);
  }

  static async _request(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    opts: RequestOpts
  ) {
    // Prepare
    const requestInit = this._prepare(method, opts);
    const url = this._prepareUrl(path, opts);

    // Fake delay
    const delay = this.fakeDelay();
    if (delay > 0) {
      await sleep(delay);
    }

    // Perform HTTP request
    let response = await fetch(url, requestInit);

    // Check for errors
    await this._checkErrors(response);

    // Good response
    return response;
  }

  private static async _checkErrors(response: Response) {
    // No errors – don't do anything
    if (response.ok) {
      return;
    }

    // HTTP status
    const status = response.status;
    const contentType = response.headers.get('Content-Type');

    // Parse server response body
    let json = undefined;
    let html = undefined;
    let text = undefined;

    switch (contentType) {
      case 'application/json':
        json = await response.json();
        break;
      case 'text/html':
        html = await response.text();
        // html = new DOMParser().parseFromString(html, 'text/html');
        break;
      case 'text/plain':
        text = await response.text();
        break;
    }

    // Bad request. Typically happens when an UPDATE, POST or PATCH
    // request is not valid and must be resubmitted by user with changes.
    if (status === 400) {
      const errors = json?.errors ? FormErrorResponse.fromJson(json.errors) : undefined;
      throw new BadRequestError(response, errors);
    }

    // Check for unauthorized. API will then return
    // HTTP 401, but without WWW-Authenticate header
    if (status === 401) {
      this._fireUnauthorizedHooks();
      throw new AuthenticationError(response);
    }

    // Check for HTTP 403 Forbidden
    if (status === 403) {
      throw new ForbiddenError(response, json?.errors || undefined);
    }

    // Check for HTTP 404 Not found
    if (status === 404) {
      throw new NotFoundError(response, json?.errors || undefined);
    }

    // Check for HTTP 405 Method not allowed
    if (status === 405) {
      throw new MethodNotAllowedError(response, json?.errors || undefined);
    }

    // Internal server error
    if (status >= 500 && status < 600) {
      throw new InternalServerError(response, html);
    }

    // Other types of errors
    if (!response.ok) {
      throw new HTTPError('Server responded with HTTP status ' + status, response);
    }
  }
}
